import React, { FC } from "react";
import { FormatDateOptions, useIntl } from "react-intl";
import Big from "big.js";
import getSymbolFromCurrency from "currency-symbol-map";
import { DatabaseRow } from "@Shape-Digital/kudzu-data/lib/types/common";

import Box from "../../Unknown/Box";
import Button from "../../Unknown/Button";
import Grid from "../../Unknown/Grid";
import Typography from "../../Unknown/Typography";
import Paper from "../../Unknown/Paper";
import messages from "./messages";
import useStyles from "./useStyles";
import { DEFAULT_CURRENCY_CODE } from "../../../common/constants";
import getCenterAddress from "../../../common/getCenterAddress";
import TextField from "../../Unknown/TextField";
import { SummaryPositionItem } from "./summaryPositionItem";
import { BookingAddOn } from "../AppointmentBookingContext/useBookingAddOns";

export type CenterSummary = {
  name: string;
  country: string;
  addresses: DatabaseRow<"center_addresses">[];
  state: string;
  city: string;
  postalCode: string;
  region: string;
  defaultCurrencyCode: string;
};

type AddOn = {
  name: string;
  price: number;
  quantity?: number;
};

type CenterService = {
  name: string;
  price: number;
  side?: string;
  studyIds: string[];
  addOnIds: string[];
  addOnQuantities?: Record<string, number>;
};

export interface AppointmentSummaryDetailsProps {
  selectedCenter: CenterSummary | null;
  selectedDateTime?: Date | null;
  selectedCenterServices?: CenterService[];
  selectedAddOns?: AddOn[];
  discountSum: number;
  priceWithDiscount: number;
  depositAmount: number;
  isSubmitButtonDisabled?: boolean;
  isSubmitLoading?: boolean;
  buttonSubmitText?: string;
  disableCenterShowing?: boolean;
  isCenterUser?: boolean;
  overriddenPrice?: string | null;
  overriddenTime?: string | null;
  overriddenDeposit?: string | null;
  isOverriddenPriceError?: boolean;
  isOverriddenDepositError?: boolean;
  isDaySelected?: boolean;
  centerAddOns: BookingAddOn[];
  isCouponDisabled?: boolean;
  couponCodeInput?: string;
  couponError?: string | null;
  couponAmount?: number | null;
  couponCode?: string | null;
  setCouponError?: (value: string | null) => void;
  setCouponCodeInput?: (value: string) => void;
  onCouponApply?: () => void;
  onSubmitClick?: () => void | Promise<void>;
  onPriceOverride?: (newPrice: string) => void;
  onDepositOverride?: (newDeposit: string) => void;
  onTimeOverride?: (newTime: string) => void;
}

const dateFormatOptions: FormatDateOptions = {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
};

const AppointmentSummaryDetails: FC<AppointmentSummaryDetailsProps> = ({
  selectedCenter,
  selectedDateTime,
  selectedCenterServices = [],
  selectedAddOns = [],
  discountSum,
  couponAmount,
  couponCode,
  priceWithDiscount,
  depositAmount,
  isOverriddenPriceError,
  isOverriddenDepositError,
  isSubmitButtonDisabled,
  isCouponDisabled,
  isSubmitLoading,
  buttonSubmitText,
  disableCenterShowing,
  isCenterUser,
  overriddenPrice,
  overriddenDeposit,
  overriddenTime,
  isDaySelected,
  centerAddOns,
  couponCodeInput,
  couponError,
  setCouponError,
  setCouponCodeInput,
  onCouponApply,
  onSubmitClick,
  onPriceOverride,
  onDepositOverride,
  onTimeOverride,
}) => {
  const { formatMessage, formatDate, formatTime, formatNumber } = useIntl();
  const classes = useStyles();

  const { name, defaultCurrencyCode } = selectedCenter || {};

  const isDateAndLocationShown =
    !disableCenterShowing && selectedDateTime && selectedCenter;

  const isSummaryPositionsShown =
    !!selectedAddOns.length || !!selectedCenterServices.length;

  const isDiscountShown = discountSum !== 0;

  const address = selectedCenter ? getCenterAddress(selectedCenter) : "";

  const currency = defaultCurrencyCode || DEFAULT_CURRENCY_CODE;
  const currencySymbol = getSymbolFromCurrency(currency);

  const totalPriceWithDiscountText = formatNumber(priceWithDiscount, {
    style: "currency",
    currency,
  });

  const depositAmountText = formatNumber(depositAmount, {
    style: "currency",
    currency,
  });

  const formattedCouponAmount = couponAmount
    ? formatNumber(couponAmount, {
        style: "currency",
        currency,
      })
    : null;

  const formattedCouponCode = couponCode
    ? `${formatMessage(messages.code)} ${couponCode}`
    : null;

  const sideTranslations: Record<string, string> = {
    left: formatMessage(messages.sideLeft),
    right: formatMessage(messages.sideRight),
  };

  return (
    <>
      <Grid container spacing={5} direction="column">
        {isDateAndLocationShown && (
          <Grid item>
            <Paper classes={{ root: classes.locationPaper }}>
              <Typography mb={1}>
                {formatMessage(messages.location, {
                  location: name,
                })}
              </Typography>
              <Typography mb={4} variant="body2">
                {address}
              </Typography>
              <Typography>
                {formatMessage(messages.dateTime, {
                  date: formatDate(selectedDateTime, dateFormatOptions),
                  time: formatTime(selectedDateTime),
                })}
              </Typography>
            </Paper>
          </Grid>
        )}
        {isSummaryPositionsShown && (
          <Grid item>
            {selectedCenterServices.map((service) => {
              const {
                name: summaryPositionName,
                side,
                price,
                addOnIds,
                studyIds,
                addOnQuantities,
              } = service;

              const sideTranslation = side ? sideTranslations[side] : "";

              const serviceName = sideTranslation
                ? `${summaryPositionName} (${sideTranslation})`
                : summaryPositionName;

              const servicePrice = formatNumber(price, {
                style: "currency",
                currency: defaultCurrencyCode,
              });

              return (
                <React.Fragment key={serviceName}>
                  <SummaryPositionItem
                    name={serviceName}
                    price={servicePrice}
                  />
                  {addOnIds?.map((serviceAddOnId) => {
                    const serviceAddOn = centerAddOns.find(
                      (addOn) => addOn.id === serviceAddOnId,
                    );
                    if (!serviceAddOn) return null;

                    const studiesCount = studyIds.length;
                    const quantity = addOnQuantities?.[serviceAddOnId] ?? 1;
                    const addOnPrice = Big(serviceAddOn.price)
                      .times(studiesCount)
                      .times(quantity)
                      .toNumber();

                    const serviceAddOnName = `${
                      serviceAddOn.name
                    } for ${serviceName}${quantity > 1 ? ` x${quantity}` : ""}`;
                    const serviceAddOnPrice = formatNumber(addOnPrice, {
                      style: "currency",
                      currency: defaultCurrencyCode,
                    });

                    return (
                      <SummaryPositionItem
                        key={serviceAddOnName}
                        name={serviceAddOnName}
                        price={serviceAddOnPrice}
                      />
                    );
                  })}
                </React.Fragment>
              );
            })}
            {selectedAddOns.map((summaryPosition) => {
              const { name: addOnName, price, quantity = 1 } = summaryPosition;
              const displayName =
                quantity > 1 ? `${addOnName} x${quantity}` : addOnName;
              const formattedPrice = formatNumber(price, {
                style: "currency",
                currency: defaultCurrencyCode,
              });

              return (
                <SummaryPositionItem
                  key={addOnName}
                  name={displayName}
                  price={formattedPrice}
                />
              );
            })}
          </Grid>
        )}
        <Grid item>
          {isDiscountShown && (
            <Box display="flex" justifyContent="space-between" mb={2}>
              <Typography>{formatMessage(messages.discount)}</Typography>
              <Typography>
                -
                {formatNumber(discountSum, {
                  style: "currency",
                  currency: defaultCurrencyCode,
                })}
              </Typography>
            </Box>
          )}
          {!!(couponCode && formattedCouponAmount) && (
            <Box
              display="flex"
              classes={{ root: classes.couponWrapperRoot }}
              alignItems="center"
              justifyContent="space-between"
              bgcolor="#19AF5933"
              color="#136333"
              borderRadius={1.5}
              mb={2}
              px={3}
              py={2}
            >
              <Typography fontSize={14} fontWeight={700} color="inherit">
                {formattedCouponCode}
              </Typography>
              <Typography fontSize={14} fontWeight={700} color="inherit">
                - {formattedCouponAmount}
              </Typography>
            </Box>
          )}
        </Grid>
        {!!(isCenterUser && onPriceOverride) && (
          <Grid item>
            <TextField
              label={formatMessage(messages.priceOverrideLabel)}
              onChange={({ target }) => onPriceOverride(target.value)}
              value={overriddenPrice || ""}
              fullWidth
              error={isOverriddenPriceError}
              helperText={
                isOverriddenPriceError &&
                formatMessage(messages.priceShouldBeLower)
              }
              InputProps={{
                startAdornment: (
                  <Typography mr={2} color="text.secondary">
                    {currencySymbol}
                  </Typography>
                ),
                classes: {
                  input: classes.priceOverrideInput,
                },
              }}
            />
          </Grid>
        )}
        {!!(isCenterUser && onDepositOverride) && (
          <Grid item>
            <TextField
              label={formatMessage(messages.depositOverrideLabel)}
              onChange={({ target }) => onDepositOverride(target.value)}
              value={overriddenDeposit || ""}
              fullWidth
              error={isOverriddenDepositError}
              helperText={
                isOverriddenDepositError &&
                formatMessage(messages.depositShouldBeLower)
              }
              InputProps={{
                startAdornment: (
                  <Typography mr={2} color="text.secondary">
                    {currencySymbol}
                  </Typography>
                ),
                classes: {
                  input: classes.priceOverrideInput,
                },
              }}
            />
          </Grid>
        )}
        {!!(isCenterUser && onTimeOverride) && (
          <Grid item>
            <TextField
              label={formatMessage(messages.timeOverrideLabel)}
              onChange={({ target }) => onTimeOverride(target.value)}
              value={overriddenTime || ""}
              fullWidth
              placeholder=""
              type="time"
              disabled={!isDaySelected}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                classes: {
                  input: classes.priceOverrideInput,
                },
              }}
            />
          </Grid>
        )}
        <Grid item>
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Typography fontWeight={700}>
              {formatMessage(messages.total)}
            </Typography>
            <Typography fontWeight={700}>
              {totalPriceWithDiscountText}
            </Typography>
          </Box>

          <Box display="flex" justifyContent="space-between" mb={2}>
            <Typography fontWeight={700} color="text.secondary">
              {formatMessage(messages.depositAmount)}
            </Typography>
            <Typography fontWeight={700} color="text.secondary">
              {depositAmountText}
            </Typography>
          </Box>
        </Grid>
        {!!(onCouponApply && setCouponCodeInput) && (
          <Grid item display="flex" gap={3} alignItems="center">
            <TextField
              onChange={({ target }) => {
                setCouponCodeInput(target.value);
                if (setCouponError) {
                  setCouponError(null);
                }
              }}
              value={couponCodeInput || ""}
              fullWidth
              size="small"
              error={!!couponError}
              helperText={couponError}
              placeholder={formatMessage(messages.couponCode)}
              disabled={isCouponDisabled || isSubmitLoading}
              sx={{
                "& .MuiOutlinedInput-root.Mui-disabled": {
                  "& > fieldset": {
                    borderStyle: "dashed",
                    borderColor: "rgba(0, 0, 0, 0.23)",
                  },
                },
              }}
            />
            <Button
              disabled={isCouponDisabled || isSubmitLoading}
              variant="contained"
              onClick={onCouponApply}
              size="medium"
              sx={{ height: "36px" }}
            >
              {formatMessage(messages.apply)}
            </Button>
          </Grid>
        )}
        {buttonSubmitText && onSubmitClick && (
          <Grid item>
            <Button
              type="submit"
              disabled={isSubmitButtonDisabled || isSubmitLoading}
              variant="contained"
              fullWidth
              onClick={onSubmitClick}
              size="large"
            >
              {buttonSubmitText}
            </Button>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default AppointmentSummaryDetails;
